import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, FilePdf, EnvelopeSimple, Users, CalendarBlank } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Devuelve [lunes, domingo] ISO YYYY-MM-DD de la semana que contiene `d`.
function currentWeekRange(d = new Date()) {
  const day = d.getDay();               // 0=domingo … 6=sábado
  const mondayOffset = (day + 6) % 7;   // lunes=0
  const monday = new Date(d);
  monday.setDate(d.getDate() - mondayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const iso = (x) => x.toISOString().slice(0, 10);
  return [iso(monday), iso(sunday)];
}

const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ContractorPayrollDialog({ open, onOpenChange, canReports }) {
  const [wk0, wk1] = currentWeekRange();
  const [dateFrom, setDateFrom] = useState(wk0);
  const [dateTo, setDateTo] = useState(wk1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [expanded, setExpanded] = useState({});    // worker_id → boolean

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const r = await api.get(`/finance/contractor-payroll?date_from=${dateFrom}&date_to=${dateTo}`);
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); setData(null); }
    finally { setLoading(false); }
  }, [open, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = () => {
    api.get(`/finance/contractor-payroll/pdf?date_from=${dateFrom}&date_to=${dateTo}`, { responseType: "blob" })
      .then((res) => {
        const blob = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = blob; a.download = `${data?.folio || "nomina"}.pdf`;
        a.click();
        URL.revokeObjectURL(blob);
      })
      .catch((e) => toast.error(apiError(e)));
  };

  const downloadWorkerPdf = async (w) => {
    if (!w.worker_id) {
      toast.warning("Este trabajador no tiene ID vinculado en Nómina — vincúlalo desde RRHH para poder generar su PDF");
      return;
    }
    try {
      const res = await api.get(
        `/finance/contractor-payroll/worker-pdf?date_from=${dateFrom}&date_to=${dateTo}&worker_id=${encodeURIComponent(w.worker_id)}`,
        { responseType: "blob" },
      );
      const blob = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blob;
      a.download = `${data?.folio || "recibo"}-${(w.name || "trabajador").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(blob);
    } catch (e) { toast.error(apiError(e)); }
  };

  const publishInApp = async () => {
    if (!data) return;
    const publishable = (data.workers || []).filter((w) => w.worker_id).length;
    const orphans = (data.workers || []).length - publishable;
    if (publishable === 0) {
      toast.warning("Ninguno de los trabajadores tiene ID vinculado en Nómina");
      return;
    }
    const msg = orphans > 0
      ? `Publicar ${publishable} recibo(s) en la plataforma. ${orphans} sin ID vinculado quedarán omitidos.`
      : `Publicar ${publishable} recibo(s) en la plataforma. Cada trabajador podrá descargar su PDF desde Mi Trabajo.`;
    if (!window.confirm(msg)) return;
    setPublishing(true);
    try {
      const r = await api.post("/finance/contractor-payroll/publish", {
        date_from: dateFrom, date_to: dateTo,
      });
      toast.success(`Publicado ${r.data.count} recibo(s) en la plataforma ✓`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setPublishing(false); }
  };

  const sendConsolidated = async () => {
    const to = window.prompt("Email destino (contabilidad / cliente):");
    if (!to) return;
    setSending(true);
    try {
      const r = await api.post("/finance/contractor-payroll/send", {
        date_from: dateFrom, date_to: dateTo, per_worker: false, to: [to.trim()],
      });
      toast.success(`Enviado a ${r.data.results[0]?.to?.join(", ")}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const totals = data?.totals || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-6xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-slate-900">
            <Users size={18} weight="bold" className="text-blue-900" />
            Nómina Contratista Semanal · {data?.folio || "…"}
          </DialogTitle>
        </DialogHeader>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 pb-3 border-b border-slate-200">
          <div>
            <Label className="overline-label flex items-center gap-1"><CalendarBlank size={11} weight="bold" /> Desde</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="h-8 w-40" data-testid="cp-date-from" />
          </div>
          <div>
            <Label className="overline-label">Hasta</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="h-8 w-40" data-testid="cp-date-to" />
          </div>
          <Button variant="outline" size="sm" onClick={() => { const [a, b] = currentWeekRange(); setDateFrom(a); setDateTo(b); }}
            data-testid="cp-this-week">Esta semana</Button>
          <Button variant="outline" size="sm" onClick={() => {
              const t = new Date(); t.setDate(t.getDate() - 7);
              const [a, b] = currentWeekRange(t); setDateFrom(a); setDateTo(b);
            }} data-testid="cp-last-week">Semana pasada</Button>
          <div className="flex-1" />
          {canReports && (
            <>
              <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5" onClick={downloadPdf} data-testid="cp-pdf">
                <FilePdf size={13} weight="bold" /> PDF consolidado
              </Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5" disabled={publishing} onClick={publishInApp} data-testid="cp-publish">
                <Users size={13} weight="bold" /> {publishing ? "Publicando…" : "Enviar a cada trabajador"}
              </Button>
              <Button size="sm" variant="outline" className="border-slate-200 gap-1.5" disabled={sending} onClick={sendConsolidated} data-testid="cp-send-consolidated">
                <EnvelopeSimple size={13} weight="bold" /> Copia consolidada
              </Button>
            </>
          )}
        </div>

        {/* Totals strip */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <p className="overline-label text-slate-500">Trabajadores</p>
            <p className="font-heading font-black text-2xl text-slate-900" data-testid="cp-total-workers">{totals.workers || 0}</p>
          </div>
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
            <p className="overline-label text-sky-700">Bruto</p>
            <p className="font-heading font-black text-2xl text-sky-800" data-testid="cp-total-gross">{money(totals.gross)}</p>
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
            <p className="overline-label text-rose-700">Descuentos (auditor)</p>
            <p className="font-heading font-black text-2xl text-rose-700" data-testid="cp-total-deductions">{money(totals.deductions)}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="overline-label text-emerald-700">NETO a pagar</p>
            <p className="font-heading font-black text-2xl text-emerald-800" data-testid="cp-total-net">{money(totals.net)}</p>
          </div>
        </div>

        {/* Workers table */}
        <div className="border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="cp-workers-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider font-bold text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Trabajador</th>
                <th className="text-left px-3 py-2">Puesto · Rol</th>
                <th className="text-left px-3 py-2">DNI</th>
                <th className="text-center px-3 py-2">Días</th>
                <th className="text-left px-3 py-2">Conceptos</th>
                <th className="text-right px-3 py-2">Bruto</th>
                <th className="text-right px-3 py-2">Desc.</th>
                <th className="text-right px-3 py-2">NETO</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={8} className="py-10 text-center text-slate-400 italic">Calculando…</td></tr>
              )}
              {!loading && (data?.workers || []).length === 0 && (
                <tr><td colSpan={8} className="py-10 text-center text-slate-400 italic">Sin registros de Operaciones en el rango</td></tr>
              )}
              {(data?.workers || []).map((w) => {
                const isOpen = !!expanded[w.worker_id || w.name];
                const rowKey = `${w.worker_id || w.name}-${w.role_in_shift}`;
                return (
                <React.Fragment key={rowKey}>
                <tr data-testid={`cp-worker-row-${w.worker_id || w.name}`}
                    className={isOpen ? "bg-slate-50" : ""}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button type="button"
                        onClick={() => setExpanded((prev) => ({ ...prev, [w.worker_id || w.name]: !isOpen }))}
                        className="text-slate-400 hover:text-slate-700 text-sm font-mono w-4"
                        data-testid={`cp-toggle-${w.worker_id || w.name}`}
                        aria-label="Expandir detalle">{isOpen ? "▾" : "▸"}</button>
                      <div>
                        <p className="font-semibold text-slate-800">{w.name}</p>
                        {w.email && <p className="text-[10px] text-slate-500">{w.email}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <p className="text-slate-700">{w.position || "-"}</p>
                    <span className={`inline-block text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${w.role_in_shift === "jumbero" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}`}>
                      {w.role_in_shift}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-600">{w.dni || "-"}</td>
                  <td className="text-center font-bold">{w.days_worked}</td>
                  <td className="px-3 py-2 text-[11px] text-slate-600 max-w-md">
                    {w.concepts.map((c) => (
                      <div key={c.rate_key}>
                        <span className="font-medium">{c.label}</span>: {c.qty} {c.unit} × ${c.rate} = <b>{money(c.amount)}</b>
                      </div>
                    ))}
                  </td>
                  <td className="text-right font-mono text-sky-700">{money(w.gross)}</td>
                  <td className="text-right font-mono text-rose-600">
                    {w.deductions_total > 0 ? `-${money(w.deductions_total)}` : "-"}
                    {w.deductions.length > 0 && (
                      <div className="text-[9px] text-slate-400 mt-0.5">
                        {w.deductions.map((d) => d.key).join(", ")}
                      </div>
                    )}
                  </td>
                  <td className="text-right font-mono font-bold text-emerald-700">
                    <div className="flex items-center justify-end gap-1.5">
                      <span>{money(w.net)}</span>
                      {canReports && w.worker_id && (
                        <button type="button" onClick={() => downloadWorkerPdf(w)}
                                className="text-slate-500 hover:text-slate-900" title="Descargar PDF individual"
                                data-testid={`cp-pdf-worker-${w.worker_id}`}>
                          <FilePdf size={13} weight="bold" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {isOpen && (
                  <tr className="bg-slate-50">
                    <td colSpan={8} className="px-3 py-3">
                      <div className="border border-slate-200 bg-white rounded-md overflow-x-auto">
                        <p className="px-3 pt-2 text-[11px] uppercase tracking-widest font-bold text-slate-600">
                          Detalle turno-por-turno · cláusulas del auditor aplicadas
                        </p>
                        <table className="w-full text-[11px]" data-testid={`cp-worker-detail-${w.worker_id || w.name}`}>
                          <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-100">
                            <tr>
                              <th className="text-left px-3 py-1.5">Fecha</th>
                              <th className="text-left px-3 py-1.5">Turno</th>
                              <th className="text-left px-3 py-1.5">Equipo</th>
                              <th className="text-left px-3 py-1.5">Área</th>
                              <th className="text-right px-3 py-1.5">Bruto</th>
                              <th className="text-left px-3 py-1.5">Cláusulas del auditor</th>
                              <th className="text-right px-3 py-1.5">Descuento</th>
                              <th className="text-right px-3 py-1.5">Neto turno</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {(w.entries || []).map((e, ix) => (
                              <tr key={e.entry_id || ix} data-testid={`cp-entry-${w.worker_id || w.name}-${ix}`}>
                                <td className="px-3 py-1.5 font-mono">{e.date}</td>
                                <td className="px-3 py-1.5">{e.shift}</td>
                                <td className="px-3 py-1.5">
                                  <span className="font-semibold">{e.equipment_type}</span>
                                  {e.equipment_code && <span className="text-slate-500 ml-1">({e.equipment_code})</span>}
                                </td>
                                <td className="px-3 py-1.5 text-slate-600">{e.area || "—"}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-sky-700">{money(e.gross)}</td>
                                <td className="px-3 py-1.5">
                                  {(e.clauses || []).length === 0 ? (
                                    <span className="text-slate-400">—</span>
                                  ) : (
                                    <div className="flex flex-wrap gap-1">
                                      {e.clauses.map((c) => (
                                        <span key={c.key} className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">
                                          {c.key} · {c.pct}%
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-rose-600">
                                  {e.deductions_total > 0 ? `-${money(e.deductions_total)}` : "—"}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono font-bold text-emerald-700">{money(e.net)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <p className="px-3 py-2 text-[10px] italic text-slate-500 border-t border-slate-100">
                          Cada cláusula la aplica el auditor sobre el turno específico donde fue detectada.
                          El monto descontado corresponde al bruto generado por el trabajador en ese turno.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-slate-500 italic">
          Los montos se calculan agregando todos los registros de Operaciones del período y aplicando las cláusulas de riesgo marcadas por el auditor en cada captura. Los ayudantes solo cobran cuando el equipo lo contempla (anclador).
        </p>
      </DialogContent>
    </Dialog>
  );
}
